import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ANON_CLIENT_IDENTIFIER } from '../../../utils/config';
import translateClientName from '../../../utils/TranslatedPossibleClientName';

import { useActions } from '../../../hooks/useActions';
import { useInitSessionClient } from '../hooks/useInitSessionClient';
import { useSessionTreatmentConfig } from '../hooks/useSessionTreatmentConfig';

import { ModalUI } from '../../UI/Modal/Modal';
import { ButtonUi } from '../../UI/Button/Button';
import AutocompleteUi from '../../UI/Autocomplete/Autocomplete';

import { reassignClientModalStyle } from './style';

export const ReassignClientModal = (props) => {
  const { reassignModalIsOpen, closeReassignClientModal, refreshData } = props;
  const { t } = useTranslation();

  const {
    clients: { clients, isLoading: clientsIsLoading },
    sessions: { selectedSession },
  } = useSelector((state) => state);

  const {
    clearSelectedSession,
    remoteSessionReassignClient,
    vrSessionReassignClient,
  } = useActions();

  const [displayValue, setDisplayValue] = useState(null);
  const [reassignClientData, setReassignClientData] = useState(null);

  const { reassignClientModal } = reassignClientModalStyle;

  const {
    treatmentData: treatmentConfiguration,
    remoteSession,
    vrSession,
  } = useSessionTreatmentConfig();

  /**
   * Handle Autocomplete`s options render
   * @param optionProps option props
   * @param obj option text and value
   * @returns ReactNode for each Autocomplete option
   */
  const handleAutocompleteRenderOptions = useCallback(
    (optionProps, { text, value }) => (
      <li {...optionProps} key={value}>
        {translateClientName(text)}
      </li>
    ),
    [],
  );

  /**
   * Handle Autocomplete change event
   * (option selection, clear value, close...)
   * @param event
   * @param value selected option value
   */
  const onChangeClient = useCallback(
    (event, value) => {
      setDisplayValue(value);
      setReassignClientData((prev) => ({
        ...prev,
        ...treatmentConfiguration,
        clientId: value?.value,
      }));
    },
    [treatmentConfiguration],
  );

  const confirmSessionClientReassign = useCallback(async () => {
    if (remoteSession) {
      const reassignSessionResponse = await remoteSessionReassignClient(
        reassignClientData,
        selectedSession?.id,
      );
      const isError = { ...reassignSessionResponse }.response?.data.error;
      if (!isError) closeReassignClientModal();
    }
    if (vrSession) {
      const reassignSessionResponse = await vrSessionReassignClient(
        reassignClientData,
        selectedSession?.id,
      );
      const isError = { ...reassignSessionResponse }.response?.data.error;
      if (!isError) closeReassignClientModal();
    }
    if (refreshData) refreshData();
  }, [
    remoteSession,
    vrSession,
    reassignClientData,
    selectedSession,
    closeReassignClientModal,
    refreshData,
    remoteSessionReassignClient,
    vrSessionReassignClient,
  ]);

  /**
   * Client's Autocomplete options
   */
  const options = useMemo(
    () =>
      clients
        .filter(
          (client) =>
            client.name !== ANON_CLIENT_IDENTIFIER ||
            client?.id === displayValue?.value,
        )
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          }),
        )
        .map((client) => ({ text: client.name, value: client.id })),
    [clients, displayValue?.value],
  );

  useInitSessionClient(displayValue, setDisplayValue, reassignModalIsOpen);

  useEffect(
    () => () => {
      setDisplayValue(null);
      clearSelectedSession();
    },
    [],
  );

  /**
   * Fetch the products data
   */
  // useFetchAction(getAvailableProducts, selectedSession.organizationId);

  return (
    <ModalUI
      modalSx={reassignClientModal}
      open={reassignModalIsOpen}
      isCloseBtn={false}
      onCloseHandler={closeReassignClientModal}
      className='reassign-modal'
      modalTitle={t('alerts.titles.reassign-sessions-client')}
    >
      <Box className='content'>
        <Typography className='content-item' variant='h6'>
          {t('alerts.confirm.reassign-sessions-client')}
        </Typography>

        <AutocompleteUi
          className={clientsIsLoading ? 'loading' : ''}
          label={t(
            'create-treatment-wizard.select-client.client-dropdown.label',
          )}
          placeholder={t(
            'create-treatment-wizard.select-client.client-dropdown.placeholder',
          )}
          autocompleteValue={displayValue}
          onAutocompleteChangeHandler={onChangeClient}
          onAutocompleteRenderOptionsHandler={handleAutocompleteRenderOptions}
          loading={clientsIsLoading}
          options={options}
        />
        <Box className='content-footer'>
          <ButtonUi
            disabled={!displayValue}
            className='main-btn'
            variant='outlined'
            type='input'
            size='small'
            onClickHandler={confirmSessionClientReassign}
          >
            {t('buttons.confirm')}
          </ButtonUi>

          <ButtonUi
            className='secondary-btn'
            variant='outlined'
            type='secondary'
            size='small'
            onClickHandler={closeReassignClientModal}
          >
            {t('buttons.cancel')}
          </ButtonUi>
        </Box>
      </Box>
    </ModalUI>
  );
};
